import { graphql } from 'gatsby';

import { SEO } from 'components';
import Blogs from 'components/Blogs';
import { Container, Section } from 'components/GridV2';
import { Hero } from 'components/pages/account';
import CoverflowCarousel from 'components/rebrand/CoverflowCarousel';
import { FAQ } from 'components/rebrand/FAQ';
import { Layout } from 'components/rebrand/Layout';
import { LinkBanner } from 'components/rebrand/LinkBanner';
import OverflowCarousel from 'components/rebrand/OverflowCarousel';
import { Perks } from 'components/rebrand/Perks';
import { SignUp } from 'components/rebrand/SignUp';
import TestimonialsScrolling from 'components/rebrand/TestimonialsScrolling';
import Tiers from 'components/rebrand/Tiers';

import { IAccountsPageProps } from 'typescriptDefs/pages/accounts';

import { afTrackingByPage } from 'helpers/getAfTracking';

const AccountsPage = ({
  data: { page },
  pageContext: { linkCanonical, pageIsMultilingual, pageName, schemaMarkupJson, variant, locale },
  ...props
}: IAccountsPageProps) => {
  const tracking = {
    queryParams: afTrackingByPage('ACCOUNTS', page.hero.cta.registrationAdsetId),
    trackEventProps: {
      category: pageName,
      label: 'Accounts Hero',
      locale,
      location: variant,
    },
  };

  return (
    <Layout
      location={props.location}
      pageIsMultilingual={pageIsMultilingual}
      pageName={pageName}
      pageVariant={variant}
      navSettings={{
        banner: ({ showSubheading }: { showSubheading: boolean }) => (
          <LinkBanner
            heading={{ en: 'Find your perfect plan', fr: 'Découvrez votre plan idéal' }[locale]}
            showSubheading={showSubheading}
            subheading={
              {
                en: 'Take our quiz to find the best plan for you.',
                fr: 'Répondez à notre quiz pour découvrir le plan qui vous convient le mieux.',
              }[locale]
            }
            link={`${locale === 'fr' ? '/fr' : ''}/account-builder-quiz`}
            trackName="compare-accounts-account-builder-banner"
          />
        ),
        darkBg: true,
      }}
      footerDisclaimer={page.disclaimers}
    >
      <SEO
        canonical={linkCanonical}
        description={page.metadata.description}
        imageSrc={page.metadata.image?.asset?.gatsbyImageData?.images?.fallback?.src}
        keywords={page.metadata.keywords}
        location={props.location}
        multilingual={pageIsMultilingual}
        title={page.metadata.title}
        structuredData={schemaMarkupJson}
      />
      <Hero {...page.hero} tracking={tracking} />
      <Section className="mb-64 md:mb-96">
        <Container className="px-32 xs:px-0">
          <Perks perks={page.perks} className="md:[&_.perkContainer]:mx-auto md:[&_.perkContainer]:max-w-[160px]" />
        </Container>
      </Section>
      <OverflowCarousel
        headingStyle
        navBelowCarousel
        heading={page.featuresHeading}
        subheading={page.featuresSubheading}
        cards={page.featuresList.map((feature) => ({
          content: feature.description,
          heading: feature.heading,
          image: feature.image,
        }))}
        classNameOverrides={{
          sectionClassName: 'bg-white',
          cardClassName: 'bg-gray-50 [&_p]:text-grey-500 [&_h4]:text-primary-400 [&_h4>a]:font-medium',
          navClassName: 'md:!justify-center',
        }}
        nextNavigationClass="accounts-features-next"
        prevNavigationClass="accounts-features-prev"
        variant="primary"
      />
      <CoverflowCarousel
        heading={page.benefitsHeading}
        carouselData={page.benefitsList.map((benefit) => ({
          description: benefit.subheading,
          heading: benefit.heading,
          image: { img: benefit.image, altText: benefit.imageAltText },
        }))}
        variant="primaryLight"
      />
      <Tiers
        className="md:[&_.tiers-heading]:w-[60%]"
        heading={page.tiersHeading}
        subheading={page.tiersSubheading}
        tiers={page.compareTiers}
        pageName={pageName}
        queryParams={afTrackingByPage('ACCOUNTS')}
        toggleBenefits
      />
      <Container className="px-32 pb-24 xs:px-0 md:pb-64">
        <SignUp
          className="[&_h2]:text-white"
          btnProps={{
            buttonVariant: 'primaryInverse',
            buttonLocation: tracking.trackEventProps.label ?? 'Sign up - Accounts',
            queryParams: tracking.queryParams,
            trackEventProps: tracking.trackEventProps,
          }}
          {...page.signUp}
        />
      </Container>
      <TestimonialsScrolling heading={page.testimonialsHeading} testimonialList={page.testimonials} />
      <Section className="pb-32 pt-64 md:pb-64 md:pt-96">
        <FAQ faqItems={page.faq.faqItems} heading={page.faq.heading} subheading={page.faq.subheading} />
      </Section>
      <Section className="py-64 md:py-96 md:pb-160">
        <Blogs heading={page.blogHeading} linkText={page.blogLinkText} articles={page.blogs} />
      </Section>
    </Layout>
  );
};

export default AccountsPage;

export const queryGraphQL = graphql`
  query ($_id: String!) {
    page: sanityPageAccountsV2(_id: { eq: $_id }) {
      benefitsHeading
      benefitsList {
        heading
        image {
          asset {
            gatsbyImageData(placeholder: NONE, width: 1000)
          }
        }
        imageAltText
        subheading
      }
      blogHeading: _rawBlogHeading
      blogLinkText
      blogs {
        excerpt: _rawExcerpt
        img {
          asset {
            gatsbyImageData(placeholder: NONE, width: 600)
          }
        }
        imgAltText
        minutesToRead
        slug {
          current
        }
        subfolder
        title
      }
      disclaimers: _rawDisclaimers
      faq {
        ...sanityFAQ
      }
      featuresHeading
      featuresList {
        description: _rawDescription
        heading: _rawHeading
        image {
          altText
          img {
            asset {
              gatsbyImageData(placeholder: NONE, width: 64)
            }
          }
        }
      }
      featuresSubheading
      hero {
        cta {
          registrationAdsetId
          text
        }
        heading
        subheading
        video {
          asset {
            url
          }
        }
      }
      metadata {
        ...sanityMetadata
      }
      perks {
        description
        icon {
          asset {
            gatsbyImageData(placeholder: NONE, width: 64)
            url
          }
        }
        iconAltText
      }
      signUp {
        ...sanitySignUp
      }
      testimonials {
        altText
        img {
          asset {
            gatsbyImageData(placeholder: NONE, width: 648)
          }
        }
      }
      testimonialsHeading
      compareTiers {
        ...sanityTiersCompare
      }
      tiersHeading: _rawV2TiersHeading
      tiersSubheading: _rawV2TiersSubheading
    }
  }
`;
